/**
 * Era extraction + grouping helpers for the catalogue palette. Players think
 * in eras — a Stone Age player scrolling for residentials shouldn't see a
 * Future Era hut inline. The palette uses these helpers to:
 *
 *   - bucket each entry by the era encoded in its cityentity_id;
 *   - present groups in chronological order;
 *   - lift the player's current era to the top.
 *
 * The extractor reads the era token from the cityentity_id pattern
 * `<prefix>_<EraToken>_<rest>`. Tokens that don't match a standard age
 * (event multi-age buckets, cultural settlements, guild raids, AllAge)
 * collapse into the `'AllAge'` bucket — they're rare in the palette and
 * cluttering the chronological list with sub-eras hurts more than the
 * grouping helps.
 *
 * All era detection here is purely string-pattern. Multi-age event
 * buildings whose `components.{Era}` blocks scale per era are treated by
 * their cityentity_id era — those appear under whichever era token their id
 * carries, or AllAge if it's `MultiAge`. The deeper component-block walk
 * lives in `details.ts` for the selection panel.
 */

/** Standard FoE ages in chronological order. Used to lay out group headers
 *  and to find the player's era position relative to the rest. */
export const STANDARD_ERAS = [
  'StoneAge',
  'BronzeAge',
  'IronAge',
  'EarlyMiddleAge',
  'HighMiddleAge',
  'LateMiddleAge',
  'ColonialAge',
  'IndustrialAge',
  'ProgressiveEra',
  'ModernEra',
  'PostModernEra',
  'ContemporaryEra',
  'TomorrowEra',
  'FutureEra',
  'ArcticFuture',
  'OceanicFuture',
  'VirtualFuture',
  'SpaceAgeMars',
  'SpaceAgeAsteroidBelt',
  'SpaceAgeVenus',
  'SpaceAgeJupiterMoon',
  'SpaceAgeTitan',
  'SpaceAgeSpaceHub',
] as const

export type StandardEra = (typeof STANDARD_ERAS)[number]
export type EraGroup = StandardEra | 'AllAge'

const STANDARD_ERA_SET: ReadonlySet<string> = new Set(STANDARD_ERAS)

/**
 * Pull the era token out of a cityentity_id, mapping non-standard tokens
 * (MultiAge, cultural-settlement nationalities, GuildRaids*, anything
 * unparseable) to the catch-all 'AllAge' bucket.
 *
 * The id pattern is `<TypePrefix>_<EraToken>_<Rest>`. The type prefix is
 * one or two uppercase letters (R_, A_, GB_, etc.) — we ignore it and pull
 * the first underscore-bounded token after.
 */
export function extractEra(cityentityId: string): EraGroup {
  const m = /^[A-Z]+_([A-Za-z]+)_/.exec(cityentityId)
  if (!m) return 'AllAge'
  const token = m[1]
  if (STANDARD_ERA_SET.has(token)) return token as StandardEra
  return 'AllAge'
}

const ERA_LABELS: Record<EraGroup, string> = {
  StoneAge: 'Stone Age',
  BronzeAge: 'Bronze Age',
  IronAge: 'Iron Age',
  EarlyMiddleAge: 'Early Middle Age',
  HighMiddleAge: 'High Middle Age',
  LateMiddleAge: 'Late Middle Age',
  ColonialAge: 'Colonial Age',
  IndustrialAge: 'Industrial Age',
  ProgressiveEra: 'Progressive Era',
  ModernEra: 'Modern Era',
  PostModernEra: 'Postmodern Era',
  ContemporaryEra: 'Contemporary Era',
  TomorrowEra: 'Tomorrow Era',
  FutureEra: 'Future Era',
  ArcticFuture: 'Arctic Future',
  OceanicFuture: 'Oceanic Future',
  VirtualFuture: 'Virtual Future',
  SpaceAgeMars: 'Space Age Mars',
  SpaceAgeAsteroidBelt: 'Space Age Asteroid Belt',
  SpaceAgeVenus: 'Space Age Venus',
  SpaceAgeJupiterMoon: 'Space Age Jupiter Moon',
  SpaceAgeTitan: 'Space Age Titan',
  SpaceAgeSpaceHub: 'Space Age Space Hub',
  AllAge: 'All-Age & Special',
}

export function eraLabel(era: EraGroup): string {
  return ERA_LABELS[era]
}

export interface EraGroupEntry<T> {
  era: EraGroup
  label: string
  /** True when this group matches the player's current era. UI renders
   *  player-era groups first to surface the relevant subset. */
  isPlayerEra: boolean
  entries: T[]
}

/**
 * Bucket entries by era (chronological order, then AllAge), pinning the
 * player's era at the top so the group they care about is the first thing
 * they see. Empty groups are dropped — no point rendering "Stone Age" with
 * zero entries.
 *
 * `getEntityId` lets the caller pass any object — the palette has
 * `ResolvedBuilding` records, but we keep the helper generic so future
 * callers (e.g. an inventory era view) can reuse it.
 */
export function groupByEra<T>(
  entries: readonly T[],
  getEntityId: (entry: T) => string,
  playerEra: string | undefined,
): EraGroupEntry<T>[] {
  const buckets = new Map<EraGroup, T[]>()
  for (const entry of entries) {
    const era = extractEra(getEntityId(entry))
    const list = buckets.get(era)
    if (list) list.push(entry)
    else buckets.set(era, [entry])
  }

  const playerKey: EraGroup | null =
    playerEra && STANDARD_ERA_SET.has(playerEra) ? (playerEra as StandardEra) : null

  const order: EraGroup[] = []
  if (playerKey && buckets.has(playerKey)) order.push(playerKey)
  for (const era of STANDARD_ERAS) {
    if (era === playerKey) continue
    if (buckets.has(era)) order.push(era)
  }
  if (buckets.has('AllAge')) order.push('AllAge')

  return order.map((era) => ({
    era,
    label: eraLabel(era),
    isPlayerEra: era === playerKey,
    entries: buckets.get(era) ?? [],
  }))
}
